import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

const doors = [
  {
    id: 'glass',
    label: 'Glassedør (Klar)',
    price: 'Inkludert',
    priceNote: 'Standard',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/8f98ae849_Glassdor.png',
    description: 'Standard klart glassdør som slipper inn naturlig lys og gir innsyn til badstuen. Varmeherdet glass og aluminiumramme for lang holdbarhet.',
    pros: ['Slipper inn naturlig lys', 'Åpent og luftig inntrykk', 'Varmeherdet glass', 'Aluminiumramme — holdbar'],
    light: 'Maksimalt',
    privacy: 'Lavt',
  },
  {
    id: 'glass-bronze',
    label: 'Glassedør (Bronse)',
    price: '+5 000 kr',
    priceNote: 'Tillegg',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/310ff340d_IMG_9371.jpg',
    description: 'Bronsetonet glass gir privatliv og et eksklusivt, varmt utseende. Elegant valg som harmonerer med tremateriell og naturlige farger.',
    pros: ['Økt privatliv', 'Eksklusivt bronsetonet uttrykk', 'Harmonerer med tre og natur', 'Lys passerer, men dempet'],
    light: 'Dempet',
    privacy: 'Middels',
  },
  {
    id: 'solid',
    label: 'Tredør',
    price: '+8 000 kr',
    priceNote: 'Premium',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/35a62e42d_Tredr.jpg',
    description: 'Massiv tredør for maksimal isolasjon og en autentisk, tradisjonell badstuopplevelse. God varmebevaring og et tidløst nordisk utseende.',
    pros: ['Maksimal isolasjon', 'Autentisk nordisk uttrykk', 'God varmebevaring', 'Tidløst design'],
    light: 'Ingen',
    privacy: 'Maksimalt',
  },
];

export default function InnvendigDorer() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
        className="relative h-[500px] overflow-hidden">
        <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/310ff340d_IMG_9371.jpg"
          alt="Dører" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-sm tracking-widest font-body uppercase text-white/60 mb-4">Innvendig materialer · Artikkel 3</p>
            <h1 className="font-heading text-6xl md:text-7xl font-light leading-[1.0] text-white mb-4">Dører</h1>
            <p className="font-body text-white/80 text-xl max-w-2xl leading-relaxed">
              Klart glass, bronse eller massivt tre — døren setter stemningen
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
          <h2 className="font-heading text-4xl font-light text-[#2a1810] mb-6">Hvilke dører kan du velge?</h2>
          <p className="font-body text-lg text-foreground/70 leading-relaxed">
            Her er en kortfattet oversikt over døralternativene du kan velge mellom. Døren bestemmer lyset, privatlivet og det visuelle uttrykket.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {doors.map((door, idx) => (
            <motion.div key={door.id}
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -6 }}
              className="bg-white border border-border/20 rounded-[12px] overflow-hidden hover:shadow-xl hover:border-[#2d5a3d] transition-all">
              <div className="relative h-80 overflow-hidden bg-secondary/20 flex items-center justify-center">
                <img src={door.img} alt={door.label} className="h-full w-full object-contain p-4" />
                <div className="absolute top-4 right-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${door.priceNote === 'Standard' ? 'bg-[#2d5a3d] text-white' : door.priceNote === 'Premium' ? 'bg-[#2a1810] text-white' : 'bg-white/90 text-[#2a1810]'}`}>
                    {door.price}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-light text-[#2a1810] mb-3">{door.label}</h3>
                <p className="font-body text-foreground/70 leading-relaxed mb-4 text-base">{door.description}</p>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-secondary/30 rounded-[6px] p-3 text-center">
                    <p className="font-body text-xs text-foreground/50 uppercase tracking-widest">Lys</p>
                    <p className="font-body text-base font-medium text-[#2a1810]">{door.light}</p>
                  </div>
                  <div className="bg-secondary/30 rounded-[6px] p-3 text-center">
                    <p className="font-body text-xs text-foreground/50 uppercase tracking-widest">Privatliv</p>
                    <p className="font-body text-base font-medium text-[#2a1810]">{door.privacy}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  {door.pros.map((pro, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check size={12} className="text-[#2d5a3d] flex-shrink-0" />
                      <span className="font-body text-base text-foreground/60">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="bg-[#2a1810] text-white rounded-[12px] p-10 mb-12">
          <h3 className="font-heading text-2xl font-light mb-4">Lys vs. privatliv — finn din balanse</h3>
          <p className="font-body text-white/80 leading-relaxed">
            Klar glassdør er perfekt for dem som vil ha lyst, åpent uttrykk og se inn fra utsiden. Bronse gir en eksklusiv mellomsone med mer privatliv uten å miste lyset. Tredøren gir maksimal isolasjon og en autentisk nordisk opplevelse — men ingen utsikt.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <a href="/innvendig-gulv" className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-body text-sm tracking-widest hover:bg-foreground hover:text-background transition-all rounded-[4px]">
            <ArrowLeft size={16} /> Forrige: Gulv
          </a>
          <a href="/innvendig-vindu" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all">
            Neste: Vindu <ArrowRight size={16} />
          </a>
        </div>
      </div>
      <Footer />
    </div>
  );
}